import { useState, useCallback } from "react";
import { FaSearch, FaCheck, FaTimes, FaUser, FaEnvelope, FaUserPlus, FaUsers } from "react-icons/fa";
import { Modal, Button, Input, Table, Tag, Message, Spin } from "@/frontend/components/common";
import { Segmented } from "antd";
import { userService } from "@/frontend/services";
import type { UserResponseDTO, PaginationRequestDTO } from "@/shared/dto";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";

export interface InviteData {
  registeredUsers: UserResponseDTO[];
  externalEmails: string[];
}

interface AddParticipantsModalProps {
  visible: boolean;
  onClose: () => void;
  onInvite: (data: InviteData) => void;
  poolId: string;
  existingParticipantIds: string[];
  existingEmails?: string[];
}

export function AddParticipantsModal({
  visible,
  onClose,
  onInvite,
  existingParticipantIds,
  existingEmails = [],
}: AddParticipantsModalProps) {
  const [activeTab, setActiveTab] = useState<"registered" | "external">("registered");
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [users, setUsers] = useState<UserResponseDTO[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<UserResponseDTO[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 7,
    total: 0,
  });

  const [emailInput, setEmailInput] = useState("");
  const [externalEmails, setExternalEmails] = useState<string[]>([]);

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const addEmail = () => {
    const email = emailInput.trim().toLowerCase();
    if (!email) {
      Message.warning({ content: "Ingresa un email" });
      return;
    }

    if (!isValidEmail(email)) {
      Message.error({ content: "El formato del email no es válido" });
      return;
    }

    if (externalEmails.includes(email)) {
      Message.warning({ content: "Este email ya está en la lista" });
      return;
    }

    if (existingEmails.includes(email)) {
      Message.warning({ content: "Este email ya tiene una invitación pendiente o es participante" });
      return;
    }

    setExternalEmails((prev) => [...prev, email]);
    setEmailInput("");
  };

  const removeEmail = (email: string) => {
    setExternalEmails((prev) => prev.filter((e) => e !== email));
  };

  const searchUsers = useCallback(
    async (params?: PaginationRequestDTO) => {
      if (!searchQuery.trim()) {
        Message.warning({ content: "Ingresa un término de búsqueda" });
        return;
      }

      try {
        setLoading(true);
        setHasSearched(true);

        const response = await userService.getPaginate({
          page: params?.page ?? 1,
          pageSize: params?.pageSize ?? 7,
          search: searchQuery,
        });

        if (response.success && response.data) {
          setUsers(response.data.data);
          setPagination({
            current: response.data.pagination.page,
            pageSize: response.data.pagination.pageSize,
            total: response.data.pagination.total,
          });
        } else {
          Message.error({ content: response.error || "Error al buscar usuarios" });
        }
      } catch (error) {
        Message.error({
          content: error instanceof Error ? error.message : "Error al buscar usuarios",
        });
      } finally {
        setLoading(false);
      }
    },
    [searchQuery]
  );

  const handleTableChange = (newPagination: TablePaginationConfig) => {
    searchUsers({
      page: newPagination.current ?? 1,
      pageSize: newPagination.pageSize ?? 7,
    });
  };

  const toggleUserSelection = (user: UserResponseDTO) => {
    setSelectedUsers((prev) => {
      const isSelected = prev.some((u) => u.id === user.id);
      if (isSelected) {
        return prev.filter((u) => u.id !== user.id);
      }
      return [...prev, user];
    });
  };

  const isUserSelected = (userId: string): boolean => {
    return selectedUsers.some((u) => u.id === userId);
  };

  const isUserAlreadyParticipant = (userId: string): boolean => {
    return existingParticipantIds.includes(userId);
  };

  const handleInvite = () => {
    const hasRegisteredUsers = selectedUsers.length > 0;
    const hasExternalEmails = externalEmails.length > 0;

    if (!hasRegisteredUsers && !hasExternalEmails) {
      Message.warning({ content: "Selecciona al menos un usuario o agrega un email" });
      return;
    }

    onInvite({
      registeredUsers: selectedUsers,
      externalEmails: externalEmails,
    });
  };

  const handleClose = () => {
    setActiveTab("registered");
    setSearchQuery("");
    setUsers([]);
    setSelectedUsers([]);
    setHasSearched(false);
    setPagination({ current: 1, pageSize: 7, total: 0 });
    setEmailInput("");
    setExternalEmails([]);
    onClose();
  };

  const totalSelections = selectedUsers.length + externalEmails.length;

  const columns: ColumnsType<UserResponseDTO> = [
    {
      title: "Usuario",
      key: "user",
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <FaUser className="text-blue-600" />
          </div>
          <div>
            <div className="font-medium">{record.fullName}</div>
            <div className="text-gray-500 text-sm">@{record.username}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Tag",
      dataIndex: "userTag",
      key: "userTag",
      render: (tag: string) =>
        tag ? <Tag color="blue">#{tag}</Tag> : <span className="text-gray-400">-</span>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email: string) => <span className="text-gray-600">{email}</span>,
    },
    {
      title: "Estado",
      key: "status",
      width: 150,
      render: (_, record) => {
        if (isUserAlreadyParticipant(record.id)) {
          return <Tag color="default">Ya es participante</Tag>;
        }
        if (isUserSelected(record.id)) {
          return <Tag color="green">Seleccionado</Tag>;
        }
        return <Tag color="blue">Disponible</Tag>;
      },
    },
    {
      title: "Acción",
      key: "action",
      width: 100,
      render: (_, record) => {
        if (isUserAlreadyParticipant(record.id)) {
          return null;
        }
        return (
          <Button
            type={isUserSelected(record.id) ? "default" : "primary"}
            size="small"
            icon={isUserSelected(record.id) ? <FaTimes /> : <FaCheck />}
            onClick={() => toggleUserSelection(record)}
          >
            {isUserSelected(record.id) ? "Quitar" : "Seleccionar"}
          </Button>
        );
      },
    },
  ];

  return (
    <Modal
      title="Agregar Participantes"
      open={visible}
      onCancel={handleClose}
      width={900}
      footer={
        <div className="flex justify-between items-center">
          <div className="text-gray-600">
            {totalSelections > 0 && (
              <span>
                {totalSelections} invitación{totalSelections > 1 ? "es" : ""} preparada
                {totalSelections > 1 ? "s" : ""}
                {selectedUsers.length > 0 && externalEmails.length > 0 && (
                  <span className="text-sm ml-1">
                    ({selectedUsers.length} usuario{selectedUsers.length > 1 ? "s" : ""}, {externalEmails.length} email{externalEmails.length > 1 ? "s" : ""})
                  </span>
                )}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={handleClose}>Cancelar</Button>
            <Button
              type="primary"
              onClick={handleInvite}
              disabled={totalSelections === 0}
            >
              Enviar Invitación{totalSelections > 1 ? "es" : ""}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <Segmented
          block
          value={activeTab}
          onChange={(value) => setActiveTab(value as "registered" | "external")}
          options={[
            {
              label: (
                <div className="flex items-center gap-2 py-1">
                  <FaUsers />
                  <span>Usuarios Registrados</span>
                </div>
              ),
              value: "registered",
            },
            {
              label: (
                <div className="flex items-center gap-2 py-1">
                  <FaUserPlus />
                  <span>Invitar por Email</span>
                </div>
              ),
              value: "external",
            },
          ]}
        />

        {activeTab === "registered" && (
          <>
            <div className="flex gap-2">
              <Input
                size="large"
                placeholder="Buscar por username, tag o email..."
                prefix={<FaSearch className="text-gray-400" />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onPressEnter={() => searchUsers()}
                className="flex-1"
              />
              <Button type="primary" size="large" onClick={() => searchUsers()}>
                Buscar
              </Button>
            </div>

            {selectedUsers.length > 0 && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-2">Usuarios seleccionados:</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedUsers.map((user) => (
                    <Tag
                      key={user.id}
                      color="blue"
                      closable
                      onClose={() => toggleUserSelection(user)}
                    >
                      {user.fullName} (@{user.username})
                    </Tag>
                  ))}
                </div>
              </div>
            )}

            <Spin spinning={loading}>
              {hasSearched && (
                <Table
                  rowKey="id"
                  dataSource={users}
                  columns={columns}
                  pagination={pagination}
                  onChange={handleTableChange}
                  locale={{
                    emptyText: "No se encontraron usuarios con ese criterio de búsqueda",
                  }}
                />
              )}
              {!hasSearched && (
                <div className="text-center py-12 text-gray-500">
                  <FaSearch className="text-4xl mx-auto mb-3 opacity-30" />
                  <p>Ingresa un término de búsqueda para encontrar usuarios</p>
                </div>
              )}
            </Spin>
          </>
        )}

        {activeTab === "external" && (
          <>
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg border border-purple-200">
              <div className="flex items-start gap-3">
                <FaEnvelope className="text-purple-500 mt-1" />
                <div>
                  <h4 className="font-medium text-purple-800">Invitar por Email</h4>
                  <p className="text-sm text-purple-600">
                    Invita a personas que aún no tienen cuenta. Recibirán un email con un 
                    enlace para registrarse y unirse automáticamente al grupo.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Input
                size="large"
                placeholder="Ingresa un email..."
                prefix={<FaEnvelope className="text-gray-400" />}
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onPressEnter={addEmail}
                className="flex-1"
                type="email"
              />
              <Button type="primary" size="large" onClick={addEmail}>
                Agregar
              </Button>
            </div>

            {externalEmails.length > 0 && (
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="font-medium text-purple-800 mb-2">
                  Emails a invitar ({externalEmails.length}):
                </h4>
                <div className="flex flex-wrap gap-2">
                  {externalEmails.map((email) => (
                    <Tag
                      key={email}
                      color="purple"
                      closable
                      onClose={() => removeEmail(email)}
                    >
                      <FaEnvelope className="mr-1 inline" />
                      {email}
                    </Tag>
                  ))}
                </div>
              </div>
            )}

            {externalEmails.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <FaUserPlus className="text-4xl mx-auto mb-3 opacity-30" />
                <p>Agrega emails para invitar personas al grupo</p>
                <p className="text-sm mt-2">
                  Pueden registrarse después y serán añadidos automáticamente
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
